"use client"

import { useEffect, useState } from "react"
import { ArrowUpRight, Copy, Check } from "lucide-react"
import { apiUrl, authHeaders } from "@/lib/api"
import { fireCouponConfetti } from "@/lib/coupon-confetti"

interface Coupon {
  id: number
  codigo: string
  desconto?: string
  vantagem?: string
  discount_percentage?: number
  status?: string
  data_inicio?: string
  data_fim?: string
  created_at?: string
}

function timeAgo(date?: string): string {
  if (!date) return ""
  const min = Math.floor((Date.now() - new Date(date).getTime()) / 60000)
  if (min < 1) return "agora"
  if (min < 60) return `${min}m`
  const h = Math.floor(min / 60)
  if (h < 24) return `${h}h`
  return `${Math.floor(h / 24)}d`
}

const statusStyle: Record<string, string> = {
  ativo: "text-emerald-600 dark:text-emerald-400",
  embreve: "text-amber-600 dark:text-amber-400",
  inativo: "text-muted-foreground",
  expirado: "text-rose-600 dark:text-rose-400",
  desconhecido: "text-muted-foreground",
}
const statusLabel: Record<string, string> = {
  ativo: "ativo",
  embreve: "em breve",
  inativo: "inativo",
  expirado: "expirado",
  desconhecido: "—",
}

export default function UserActivity() {
  const [coupons, setCoupons] = useState<Coupon[]>([])
  const [loading, setLoading] = useState(true)
  const [copied, setCopied] = useState<number | null>(null)
  const [totalAtivos, setTotalAtivos] = useState(0)

  useEffect(() => {
    const token = typeof window !== "undefined" ? localStorage.getItem("access_token") : null
    const headers = authHeaders(token)
    fetch(`${apiUrl()}/coupons/?ordering=-created_at&page_size=6`, { headers })
      .then((r) => r.json())
      .then((data) => setCoupons((data.results || (Array.isArray(data) ? data : [])).slice(0, 6)))
      .catch(console.error)
      .finally(() => setLoading(false))

    fetch(`${apiUrl()}/coupons/?status=ativo&page_size=1`, { headers })
      .then((r) => r.json())
      .then((data) => setTotalAtivos(data.count || 0))
      .catch(() => {})
  }, [])

  function copyCode(e: React.MouseEvent<HTMLButtonElement>, id: number, code: string) {
    const trigger = e.currentTarget
    navigator.clipboard.writeText(code).then(() => {
      setCopied(id)
      fireCouponConfetti(trigger)
      setTimeout(() => setCopied(null), 1800)
    })
  }

  return (
    <div className="surface-card flex h-full flex-col">
      <div className="flex items-baseline justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Códigos</div>
          <h3 className="mt-1 font-display text-2xl font-normal tracking-tight text-foreground">
            Cupons recentes
          </h3>
        </div>
        <a
          href="/dashboard-cms/cupons"
          className="group inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
        >
          Ver kanban
          <ArrowUpRight className="h-3 w-3 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
        </a>
      </div>

      <div className="mt-5 flex-1">
        {loading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => <div key={i} className="h-12 animate-pulse rounded bg-muted/30" />)}
          </div>
        ) : coupons.length === 0 ? (
          <div className="flex h-full min-h-[200px] items-center justify-center text-center">
            <p className="max-w-[260px] text-sm text-muted-foreground">
              Nenhum código capturado ainda hoje. Janela mais ativa: <span className="text-foreground">09h–11h</span> BRT.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-border/40">
            {coupons.map((c) => {
              const status = c.status || "ativo"
              const discount = c.discount_percentage ? `-${c.discount_percentage}%` : c.desconto || ""
              const isCopied = copied === c.id
              return (
                <li key={c.id} className="group flex items-center gap-3 py-3 -mx-2 px-2 rounded-md transition-colors hover:bg-muted/20">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-3">
                      <span className="font-mono text-[15px] font-semibold tracking-tight text-foreground">
                        {c.codigo}
                      </span>
                      {discount && (
                        <span className="font-mono text-xs font-medium text-[var(--brand-amber)]">
                          {discount}
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 flex items-baseline gap-2 text-xs">
                      <span className={`uppercase tracking-[0.14em] text-[10px] ${statusStyle[status] || statusStyle.ativo}`}>
                        {statusLabel[status] || status}
                      </span>
                      <span className="text-muted-foreground">·</span>
                      <span className="font-mono text-muted-foreground">{timeAgo(c.created_at)}</span>
                      {c.vantagem && (
                        <>
                          <span className="text-muted-foreground">·</span>
                          <span className="truncate text-muted-foreground" title={c.vantagem}>{c.vantagem}</span>
                        </>
                      )}
                    </div>
                  </div>

                  <button
                    onClick={(e) => copyCode(e, c.id, c.codigo)}
                    className="flex-shrink-0 rounded-md border border-border/60 bg-background/60 p-1.5 text-muted-foreground transition-all hover:border-[var(--brand-amber)]/50 hover:text-foreground"
                    title="Copiar código"
                    aria-label={`Copiar ${c.codigo}`}
                  >
                    {isCopied ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </div>

      <div className="mt-5 flex items-center justify-between border-t border-border/40 pt-4 text-xs text-muted-foreground tabular-nums">
        <span><span className="text-foreground">{totalAtivos}</span> ativos agora</span>
        <a href="/dashboard-cms/cupons" className="hover:text-foreground">Gerenciar →</a>
      </div>
    </div>
  )
}
